import { ReactNode, createContext, useEffect, useMemo, useReducer } from "react";
import {
  MegaShareAction,
  MegaShareContextState,
  MegaShareParticipant,
  initialMegaShareState,
  megaShareReducer,
} from "../types/mega-share-context";
import { MegaShareFormTargetType } from "kz-ui-sdk";

interface MegaShareContextType {
  giver: MegaShareParticipant;
  taker: MegaShareParticipant;
  claimAmount: number;
  instruction: Record<string, string>;
  remainingSeconds: number;
  targetTypes: MegaShareFormTargetType[];
  dispatch: React.Dispatch<MegaShareAction>;
  setGiver: (giver: MegaShareParticipant) => void;
  setTaker: (taker: MegaShareParticipant) => void;
  setClaimAmount: (amount: number) => void;
  setInstruction: (instruction: Record<string, string>) => void;
  setRemainingSeconds: (seconds: number) => void;
  setTargetTypes: (types: MegaShareFormTargetType[]) => void;
}

export const MegaShareContext = createContext<MegaShareContextType | undefined>(undefined);

interface MegaShareProviderProps {
  children: ReactNode;
  data?: Partial<MegaShareContextState>;
}

export const MegaShareProvider = ({ children, data }: MegaShareProviderProps) => {
  const initialState = useMemo(() => ({ ...initialMegaShareState, ...data }), [data]);
  const [contextState, dispatch] = useReducer(megaShareReducer, initialState);

  // Sync external data changes to context
  useEffect(() => {
    if (data?.giver !== undefined) {
      dispatch({ type: "SET_GIVER", payload: data.giver });
    }
    if (data?.taker !== undefined) {
      dispatch({ type: "SET_TAKER", payload: data.taker });
    }
    if (data?.claimAmount !== undefined) {
      dispatch({ type: "SET_CLAIM_AMOUNT", payload: data.claimAmount });
    }
    if (data?.instruction !== undefined) {
      dispatch({ type: "SET_INSTRUCTION", payload: data.instruction });
    }
    if (data?.remainingSeconds !== undefined) {
      dispatch({ type: "SET_REMAINING_SECONDS", payload: data.remainingSeconds });
    }
    if (data?.targetTypes !== undefined) {
      dispatch({ type: "SET_TARGET_TYPES", payload: data.targetTypes });
    }
  }, [data]);

  const contextValue: MegaShareContextType = useMemo(
    () => ({
      giver: contextState.giver,
      taker: contextState.taker,
      claimAmount: contextState.claimAmount,
      instruction: contextState.instruction,
      remainingSeconds: contextState.remainingSeconds,
      targetTypes: contextState.targetTypes,
      dispatch,
      setGiver: (giver: MegaShareParticipant) => {
        dispatch({ type: "SET_GIVER", payload: giver });
      },
      setTaker: (taker: MegaShareParticipant) => {
        dispatch({ type: "SET_TAKER", payload: taker });
      },
      setClaimAmount: (amount: number) => {
        dispatch({ type: "SET_CLAIM_AMOUNT", payload: amount });
      },
      setInstruction: (instruction: Record<string, string>) => {
        dispatch({ type: "SET_INSTRUCTION", payload: instruction });
      },
      setRemainingSeconds: (seconds: number) => {
        dispatch({ type: "SET_REMAINING_SECONDS", payload: seconds });
      },
      setTargetTypes: (types: MegaShareFormTargetType[]) => {
        dispatch({ type: "SET_TARGET_TYPES", payload: types });
      },
    }),
    [contextState],
  );

  return <MegaShareContext.Provider value={contextValue}>{children}</MegaShareContext.Provider>;
};
